import React, { useState } from "react";
import DiscountBadge from "assests/images/savings-cookie.svg?react";
import { useLocale } from "lib/hooks/useLocale";
import { Bank, CaretDown } from "@phosphor-icons/react";
import { getBankIcon } from "lib/utils/coupons";
import { CouponPageConfigType } from "lib/types/merchantConfigTypes";
import { classNames } from "lib/utils/helpers";
import { hexToRgba } from "@shopflo/ui/utils";

interface BankOffersCardProps {
  couponTitle: string;
  couponHeader: string;
  couponDescription: string;
  couponLongDescription: string[];
  bankKey: string;
  customisations?: CouponPageConfigType;
}

const BankOffersCard: React.FC<BankOffersCardProps> = React.memo(
  ({
    couponTitle,
    couponHeader,
    couponDescription,
    couponLongDescription,
    bankKey,
    customisations
  }) => {
    const { t } = useLocale();
    const [showMore, setShowMore] = useState<boolean>(false);

    const bankIcon = getBankIcon(bankKey);

    return (
      <div className="group relative box-border flex h-full min-h-[8rem] w-full flex-col items-start overflow-hidden rounded-2xl border border-zinc-200 bg-white"
      style={{
        borderRadius: customisations?.cardRadius,
      }}
    >
      <div
        className={classNames(
          "flex w-full flex-row items-center justify-between py-2 pl-3 pr-2",
        )}
        style={{
          background: `linear-gradient(to bottom, ${hexToRgba(customisations?.primaryColor ?? '#000000', 0.2)}, #FFF)`,
        }}>
        <div className="flex flex-row items-center gap-1">
          <div className="flex items-center justify-start">
          {Boolean(bankIcon) ? <img src={bankIcon} alt="Bank Icon" className="object-contain object-center w-6 h-6 pt-1" />
                : <Bank className="mr-2 h-6 w-6 stroke-primary-dark text-primary-dark" />}
            </div>

          <h3 className="truncate pr-2 text-text-sm-semibold uppercase">
            {couponTitle}
          </h3>
        </div>
      </div>

      <div className="group relative flex min-h-[4.5rem] w-full flex-col items-start justify-between gap-2 overflow-x-hidden px-3">
        <div className="flex flex-col">
          <div className="flex flex-col">
            <h3 className="text-text-xs-semibold" style={{ color: 'var(--flo-coal-dark-color)' }}>{couponHeader}</h3>
            {couponDescription && Boolean(couponDescription.length) && (
              <p className="text-text-xs-medium" style={{ color: 'var(--flo-coal-light-color)' }}>{couponDescription}</p>
            )}
          </div>
        </div>

        {couponLongDescription && couponLongDescription.length > 0 && (
          showMore ? (
            <>
              <ul className="flex w-full flex-col space-y-1 rounded-lg bg-gray-lighter px-2 py-3">
                {couponLongDescription?.map((point: string, index: number) => (
                  <li
                    className="ml-4 list-disc text-xs font-normal text-coal-light"
                    key={`Callout-Card-${index}`}>
                    <p>{point}</p>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => setShowMore(false)}
                className="w-fit text-text-xxs-medium outline-none"
                style={{ color: 'var(--flo-coal-dark-color)' }}>
                Hide details
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setShowMore(true)}
              className="w-fit text-text-xxs-medium outline-none text-zinc-500">
              {t("more")}
            </button>
          )
        )}
      </div>
    </div>
    );
  },
);

export default BankOffersCard;
